import React from 'react';
import { Link } from 'react-router-dom';
import { FaRegQuestionCircle } from 'react-icons/fa';
import MetaGroup from '../../components/meta-group';

const SUBSCRIPTION_ITEMS = [
    {
        item: 'ServiceDesk (non-legacy)',
        standard: '$199/mo'
    },
    {
        item: 'SD-Mobile (technician app)',
        standard: '$32/user/mo'
    },
    {
        item: 'SD-CyberOffice (communication automation)',
        standard: 'Click here',
        standardLink: 'https://help.rossware.com/en/articles/733120'
    },
    {
        item: 'DispatchLinks',
        standard: '$29/mo/integration',
        subRows: [
            {
                item: 'ServiceBench, ServicePower, Service Live, DispatchMe, BlueBook-Sync+, American Home Shield, LG, Samsung',
                standard: ''
            }
        ]
    },
    {
        item: 'SD-RevenueBuilder (recurring service)',
        standard: '$49/mo'
    },
    {
        item: 'SD-Dealer (merchandise sales)',
        standard: '$49/mo'
    },
    {
        item: 'Server Hosting',
        standard: '',
        subRows: [
            { item: 'Tier 1 (recommended 1-3 users)', standard: '$299/mo' },
            { item: 'Tier 2 (recommended 3-7 users)', standard: '$399/mo' },
            { item: 'Tier 3 (recommended 7-12 users)', standard: '$499/mo' },
            { item: 'Tier 4 (recommended 12-25 users)', standard: '$699/mo' },
            { item: 'Tier 5 (recommended 25+ users)', standard: '$1499/mo' }
        ]
    },
    {
        item: 'SmartParts',
        standard: '$35/mo'
    },
    {
        item: 'AHSU',
        standard: '$10/mo'
    },
    {
        item: 'Legacy Support (updates and product support)',
        standard: '$89/mo'
    }
];

const styles = {
    page: {
        background: 'linear-gradient(180deg, #f6f9ff 0%, #ffffff 48%)',
        minHeight: 'calc(100vh - 180px)'
    },
    headerCard: {
        backgroundColor: '#fff',
        border: '1px solid var(--border-light)',
        borderRadius: '16px',
        boxShadow: '0 20px 45px -35px rgba(12, 23, 84, 0.35)',
        padding: '28px'
    },
    tableWrap: {
        backgroundColor: '#fff',
        border: '1px solid var(--border-light)',
        borderRadius: '16px',
        boxShadow: '0 20px 45px -35px rgba(12, 23, 84, 0.35)',
        overflow: 'hidden'
    },
    noteBox: {
        background: 'linear-gradient(135deg, #d8fbe6 0%, #baf5d5 100%)',
        border: '1px solid #7ed8a7',
        borderLeft: '8px solid #0f9d58',
        borderRadius: '14px',
        padding: '1.15rem 1.25rem',
        boxShadow: '0 18px 36px -28px rgba(15, 157, 88, 0.85)'
    },
    noteBadge: {
        display: 'inline-block',
        backgroundColor: '#0f9d58',
        color: '#fff',
        fontSize: '0.72rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderRadius: '999px',
        padding: '0.2rem 0.6rem',
        marginBottom: '0.55rem',
        fontWeight: 700
    },
    noteText: {
        margin: 0,
        fontSize: '1.05rem',
        fontWeight: 600,
        color: '#084c2a',
        lineHeight: 1.35
    },
    noteHighlight: {
        fontWeight: 800,
        fontSize: '1.16rem'
    },
    noteLink: {
        color: '#05603a',
        textDecoration: 'underline'
    },
    noteEmailLink: {
        color: '#05603a',
        textDecoration: 'underline',
        fontWeight: 700
    },
    subRowItem: {
        color: '#6c757d',
        paddingLeft: '3.5rem',
        backgroundColor: '#f6f8fb',
        fontSize: '0.95rem'
    },
    subRowPrice: {
        color: '#6c757d',
        backgroundColor: '#f6f8fb',
        fontSize: '0.95rem'
    },
    infoIcon: {
        marginLeft: '0.45rem',
        color: '#6c757d',
        verticalAlign: 'middle',
        cursor: 'help'
    }
};

const PricingSchedule2026 = () => (
    <div style={styles.page} className="pt-4 pb-6">
        <style>{`
            .pricing-info-tooltip-wrap {
                position: relative;
                display: inline-flex;
                align-items: center;
            }

            .pricing-info-tooltip {
                position: absolute;
                left: calc(100% + 10px);
                top: 50%;
                transform: translateY(-50%);
                min-width: 280px;
                max-width: 360px;
                background: #1f2937;
                color: #fff;
                padding: 8px 10px;
                border-radius: 8px;
                font-size: 0.82rem;
                line-height: 1.35;
                box-shadow: 0 10px 24px -12px rgba(0, 0, 0, 0.65);
                opacity: 0;
                visibility: hidden;
                pointer-events: none;
                transition: opacity 80ms ease, visibility 80ms ease;
                z-index: 20;
            }

            .pricing-info-tooltip-wrap:hover .pricing-info-tooltip,
            .pricing-info-tooltip-wrap:focus-within .pricing-info-tooltip {
                opacity: 1;
                visibility: visible;
            }
        `}</style>
        <MetaGroup
            title="Pricing Schedule 2026 | Rossware"
            metaDescription="Subscription pricing schedule for 2026 with standard rates."
            ogImage=""
            canonical="pricing-schedule-2026"
        />

        <section>
            <div className="container-sm">
                <div style={styles.headerCard} className="mb-4">
                    <p className="pre-text mb-2">2026 Pricing</p>
                    <h1 className="mb-3">Pricing Schedule 2026</h1>
                    <p className="mb-0 text-muted">
                        Subscription pricing for each ServiceDesk add-on using standard rates.
                    </p>
                </div>

                <div style={styles.tableWrap}>
                    <div className="table-responsive">
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th scope="col">Subscription Item</th>
                                    <th scope="col">Standard Pricing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SUBSCRIPTION_ITEMS.map((row) => (
                                    <React.Fragment key={row.item}>
                                        <tr>
                                            <td>
                                                {row.item}
                                                {row.item === 'ServiceDesk (non-legacy)' && (
                                                    <span className="pricing-info-tooltip-wrap">
                                                        <FaRegQuestionCircle
                                                            aria-label="More info"
                                                            style={styles.infoIcon}
                                                        />
                                                        <span className="pricing-info-tooltip">
                                                            Customers who purchased ServiceDesk prior to June 2023 are on an alternate fee.
                                                        </span>
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {row.standardLink ? (
                                                    <a
                                                        href={row.standardLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="underlined"
                                                        style={{ textDecoration: 'underline' }}
                                                    >
                                                        {row.standard}
                                                    </a>
                                                ) : row.standard}
                                            </td>
                                        </tr>
                                        {row.subRows && row.subRows.map((subRow) => (
                                            <tr key={`${row.item}-${subRow.item}`}>
                                                <td style={styles.subRowItem}>{subRow.item}</td>
                                                <td style={styles.subRowPrice}>{subRow.standard}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={styles.noteBox} className="mt-4">
                    <div style={styles.noteBadge}>Savings</div>
                    <p style={styles.noteText}>
                        Users of <Link to="/rosswarepay" style={styles.noteLink}>RosswarePay</Link> recieve a discount on some subscription items,
                        contact <a href="mailto:billing@rossware.net" style={styles.noteEmailLink}>billing@rossware.net</a> to learn more.
                    </p>
                </div>
            </div>
        </section>
    </div>
);

export default PricingSchedule2026;
